import { useMemo, useState } from 'react'
import { TrendChart } from '../components/TrendChart'
import { MetricStrip } from '../components/MetricStrip'
import { DataFreshnessBadge } from '../components/DataFreshnessBadge'
import {
  advertisingRecordComplete,
  advertisingRecordTotal,
  advertisingSourceAvailable,
  summarizeAdvertisingProfit,
} from '../advertising'
import { useProductData } from '../data'
import { JD_STORE_ID, productMasterCostDefaults, productMasterRecordMap } from '../productMaster'
import { internalProductRecordsByCode, summarizeProductProfit } from '../productProfit'
import { statisticsPeriodOptions, statisticsWindow, type StatisticsPeriod, visibleDateRange } from '../statistics'
import { metricsAcrossDates, money, percent } from '../utils'

const sourceStatusLabel = {
  provisional: '暂估',
  settled: '已结算',
} as const

export function AdvertisingPage() {
  const {
    advertisingCosts,
    data,
    internalProductInfo,
    productMaster,
    segmentIndex,
    selectedDate,
  } = useProductData()
  const [statisticsPeriod, setStatisticsPeriod] = useState<StatisticsPeriod>('day')
  const productMasterRecords = useMemo(() => productMasterRecordMap(productMaster, JD_STORE_ID), [productMaster])
  const costDefaults = useMemo(() => productMasterCostDefaults(productMaster, JD_STORE_ID), [productMaster])
  const companyRecordsByCode = useMemo(
    () => internalProductRecordsByCode(internalProductInfo?.records || []),
    [internalProductInfo],
  )
  const period = useMemo(
    () => data ? statisticsWindow(statisticsPeriod, selectedDate, data.meta.dates) : null,
    [data, selectedDate, statisticsPeriod],
  )
  const view = useMemo(() => {
    if (!data || !period) return null
    const spus = data.products.filter((product) => product.level === 'spu')
    const skus = data.products.filter((product) => product.level === 'sku')
    const gmv = spus.reduce(
      (sum, product) => sum + metricsAcrossDates(product, period.dates).gmv,
      0,
    )
    const profit = summarizeProductProfit(
      skus,
      period.dates,
      selectedDate,
      productMasterRecords,
      costDefaults,
      companyRecordsByCode,
      segmentIndex,
    )
    return {
      profit,
      summary: summarizeAdvertisingProfit(
        advertisingCosts,
        period.dates,
        gmv,
        profit.profitWithRebate,
      ),
    }
  }, [
    advertisingCosts,
    companyRecordsByCode,
    costDefaults,
    data,
    period,
    productMasterRecords,
    segmentIndex,
    selectedDate,
  ])
  const trend = useMemo(() => (advertisingCosts?.dailyRecords || [])
    .filter((record) =>
      record.date <= selectedDate && advertisingSourceAvailable(record.regularJzt))
    .slice()
    .sort((left, right) => left.date.localeCompare(right.date))
    .slice(-30)
    .map((record) => ({ date: record.date, value: record.regularJzt.amount })), [
    advertisingCosts,
    selectedDate,
  ])

  if (!data || !period || !view || !advertisingCosts) return null
  const { profit, summary } = view
  const range = visibleDateRange(period.dates)
  const coverageText = `广告费用覆盖 ${summary.coveredDays}/${summary.requestedDays} 天`
  const currentContract = advertisingCosts.brandContracts.find((contract) =>
    selectedDate >= contract.validFrom && selectedDate <= contract.validTo,
  )
  const latestRecord = advertisingCosts.dailyRecords
    .slice()
    .sort((left, right) => right.date.localeCompare(left.date))[0]
  const collectionRows = latestRecord ? [
    {
      name: '常规京准通',
      source: latestRecord.regularJzt,
      cadence: 'T+1采集，D+3/D+15回补',
      finalRule: '财务记录消费为最终值',
    },
    {
      name: '品牌合约',
      source: latestRecord.brandContract,
      cadence: '每日采集，合同变化时复核季度金额',
      finalRule: '当日实际执行费用进入利润',
    },
    {
      name: '京东联盟',
      source: latestRecord.jdAlliance,
      cadence: 'T+1采集，结算后回填',
      finalRule: '结算费用为财务优先口径',
    },
  ] : []
  return (
    <div className="advertising-page page-stack">
      <header className="page-title-row">
        <div>
          <h1>广告费用与利润</h1>
          <p>{period.label} · {range} · 店铺级广告成本统一核算</p>
        </div>
        <div className="page-title-actions">
          <DataFreshnessBadge date={latestRecord?.date} label="广告费用截至" />
          <div className="metric-switch" role="group" aria-label="广告统计时间范围">
            {statisticsPeriodOptions.map((item) => (
              <button
                type="button"
                key={item.key}
                className={item.key === statisticsPeriod ? 'is-active' : ''}
                aria-pressed={item.key === statisticsPeriod}
                onClick={() => setStatisticsPeriod(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {!summary.complete && (
        <div className="advertising-warning">
          完整广告费用仅覆盖 {summary.coveredDays}/{summary.requestedDays} 天；常规京准通已覆盖 {summary.regularJztCoveredDays}/{summary.requestedDays} 天。
          品牌合约和京东联盟缺失日期不补0，完整广告费和广告后利润仍显示为“—”。
        </div>
      )}

      <section className="advertising-section advertising-overview">
        <div className="section-heading-row">
          <div><h2>广告后经营结果</h2><p>商品含返点利润－常规京准通－品牌合约－京东联盟</p></div>
          <span>{coverageText}</span>
        </div>
        <MetricStrip items={[
          {
            label: '成交金额',
            value: money(summary.gmv),
            context: '商智商品成交口径',
          },
          {
            label: '广告前利润',
            value: summary.profitBeforeAdvertising === null ? '—' : money(summary.profitBeforeAdvertising),
            context: profit.context,
          },
          {
            label: '广告费',
            value: summary.complete ? money(summary.total) : '—',
            context: summary.complete ? `${summary.provisionalDays}天含暂估值` : coverageText,
          },
          {
            label: '广告后利润',
            value: summary.profitAfterAdvertising === null ? '—' : money(summary.profitAfterAdvertising),
            context: '店铺级，未分摊到SPU/SKU',
          },
          {
            label: '广告费率',
            value: summary.advertisingRate === null ? '—' : percent(summary.advertisingRate * 100),
            context: '广告费 ÷ 成交金额',
          },
          {
            label: '广告后利润率',
            value: summary.profitAfterAdvertisingMargin === null
              ? '—'
              : percent(summary.profitAfterAdvertisingMargin * 100),
            context: '广告后利润 ÷ 成交金额',
          },
        ]} />
      </section>

      <section className="advertising-section">
        <div className="section-heading-row"><div><h2>费用构成</h2><p>季度合约金额不重复进入当日利润</p></div></div>
        <MetricStrip items={[
          {
            label: '常规京准通',
            value: summary.regularJztCoveredDays === summary.requestedDays ? money(summary.regularJzt) : '—',
            context: `覆盖 ${summary.regularJztCoveredDays}/${summary.requestedDays} 天，最终值以财务记录为准`,
          },
          {
            label: '品牌合约',
            value: summary.brandContractCoveredDays === summary.requestedDays ? money(summary.brandContract) : '—',
            context: `覆盖 ${summary.brandContractCoveredDays}/${summary.requestedDays} 天，按实际执行费用`,
          },
          {
            label: '京东联盟',
            value: summary.jdAllianceCoveredDays === summary.requestedDays ? money(summary.jdAlliance) : '—',
            context: `覆盖 ${summary.jdAllianceCoveredDays}/${summary.requestedDays} 天，结算费用优先`,
          },
        ]} />
      </section>

      <section className="advertising-section">
        <div className="section-heading-row">
          <div><h2>每日采集清单</h2><p>每天抓什么、从哪里抓、什么时候回填最终值</p></div>
        </div>
        <div className="table-responsive">
          <table className="advertising-source-table">
            <thead>
              <tr><th>费用类型</th><th>来源页面</th><th>查询与粒度</th><th>采集时间</th><th>页面更新时间</th><th>更新节奏</th><th>最终口径</th></tr>
            </thead>
            <tbody>{collectionRows.map((item) => (
              <tr key={item.name}>
                <td><strong>{item.name}</strong></td>
                <td>{item.source.pageName}<small>{item.source.pageId}</small></td>
                <td>{item.source.queryRange}<small>{item.source.granularity} · {item.source.attribution}</small></td>
                <td>{item.source.collectedAt}</td>
                <td>{item.source.pageUpdatedAt}</td>
                <td>{item.cadence}</td>
                <td>{item.finalRule}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </section>

      <section className="advertising-section">
        <div className="section-heading-row"><h2>常规京准通花费趋势</h2><span>近30个已采集日期</span></div>
        {trend.length
          ? <TrendChart
              data={trend}
              valueFormatter={(value) => money(value)}
              valueLabel="常规京准通"
              ariaLabel="近30天常规京准通花费趋势"
            />
          : <div className="empty-state">暂无广告费用记录。</div>}
      </section>

      <section className="advertising-section">
        <div className="section-heading-row">
          <div><h2>每日费用与证据</h2><p>暂估值在财务或结算数据到齐后回填，不覆盖来源口径</p></div>
        </div>
        <div className="table-responsive">
          <table className="advertising-daily-table">
            <thead>
              <tr><th>日期</th><th>常规京准通</th><th>品牌合约</th><th>京东联盟</th><th>广告费合计</th><th>数据状态</th></tr>
            </thead>
            <tbody>{advertisingCosts.dailyRecords
              .slice()
              .sort((left, right) => right.date.localeCompare(left.date))
              .map((record) => {
                const statuses = [record.regularJzt, record.brandContract, record.jdAlliance]
                const missing = statuses.some((item) => item.status === 'missing')
                const provisional = !missing && statuses.some((item) => item.status === 'provisional')
                const sourceValue = (source: typeof record.regularJzt) =>
                  advertisingSourceAvailable(source) ? money(source.amount) : '—'
                return (
                  <tr key={record.date}>
                    <td>{record.date}</td>
                    <td className="numeric" title={`${record.regularJzt.pageName} · ${record.regularJzt.attribution}`}>{sourceValue(record.regularJzt)}</td>
                    <td className="numeric" title={`${record.brandContract.pageName} · ${record.brandContract.attribution}`}>{sourceValue(record.brandContract)}</td>
                    <td className="numeric" title={`${record.jdAlliance.pageName} · ${record.jdAlliance.attribution}`}>{sourceValue(record.jdAlliance)}</td>
                    <td className="numeric">{advertisingRecordComplete(record) ? money(advertisingRecordTotal(record)) : '—'}</td>
                    <td>
                      <span className={`advertising-status ${missing ? 'is-missing' : provisional ? 'is-provisional' : 'is-settled'}`}>
                        {missing ? '缺部分来源' : provisional ? '含暂估' : sourceStatusLabel.settled}
                      </span>
                    </td>
                  </tr>
                )
              })}</tbody>
          </table>
        </div>
      </section>

      {currentContract && (
        <section className="advertising-section">
          <div className="section-heading-row">
            <div><h2>品牌合约预算核对</h2><p>{currentContract.name} · {currentContract.validFrom} 至 {currentContract.validTo}</p></div>
            <span>合同 {money(currentContract.contractAmount)} · {currentContract.paymentStatus}</span>
          </div>
          <div className="contract-quarter-grid">
            {currentContract.quarters.map((quarter) => (
              <article key={quarter.quarter} className={selectedDate >= quarter.validFrom && selectedDate <= quarter.validTo ? 'is-current' : ''}>
                <span>{quarter.quarter}</span>
                <strong>{money(quarter.amount)}</strong>
                <small>{quarter.validFrom} 至 {quarter.validTo}</small>
              </article>
            ))}
          </div>
          <p className="advertising-contract-note">
            季度订单金额用于预算、付款和执行进度核对，不直接除以天数作为利润成本；利润只扣每日实际执行费用。
            来源：{currentContract.source.pageName} · {currentContract.source.queryRange} · 采集于 {currentContract.source.collectedAt}。
          </p>
        </section>
      )}

      <footer className="methodology-note">
        广告后利润仍是经营估算：商品利润只覆盖成本资料完整的成交SKU；广告费用完整覆盖后才计算，缺失日期不按0处理。常规京准通最终消费以财务记录为准，京东联盟以结算数据为财务口径。品牌合约暂不分摊到SPU/SKU。
      </footer>
    </div>
  )
}
